import React, { useState, useRef, useEffect } from 'react';
import ContactFormModal from '../../modals/ContactFormModal';
import './FloatingContactButton.css';

// SuccessModal — thank-you toast. Auto-dismisses after 10s (WCAG 2.2.1: long
// enough to read, and the dismiss timer pauses while the modal has focus or
// hover so users with extra reading time aren't cut off).
const SuccessModal = ({ onClose }) => {
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const timer = setTimeout(() => {
      onClose();
    }, 10000);
    return () => clearTimeout(timer);
  }, [onClose, paused]);

  return (
    <div className="SuccessModal-overlay">
      <div
        className="SuccessModal-container"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="success-modal-header"
        aria-describedby="success-modal-message"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        tabIndex="-1"
      >
        <button
          type="button"
          className="SuccessModal-close"
          onClick={onClose}
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <h2 id="success-modal-header" className="SuccessModal-header">Thank You!</h2>
        <p id="success-modal-message" className="SuccessModal-message">
          Your message has been sent. We'll be in touch shortly.
        </p>
      </div>
    </div>
  );
};

const FloatingContactButton = () => {
  const [isFormVisible, setFormVisible] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const formRef = useRef(null);
  const buttonRef = useRef(null);

  const toggleFormVisibility = () => {
    setFormVisible((prev) => !prev);
  };

  const closeForm = (e) => {
    // Close if click occurs outside both the form and the button.
    if (
      formRef.current &&
      !formRef.current.contains(e.target) &&
      buttonRef.current &&
      !buttonRef.current.contains(e.target)
    ) {
      setFormVisible(false);
    }
  };

  useEffect(() => {
    if (isFormVisible) {
      document.addEventListener('mousedown', closeForm);
    } else {
      document.removeEventListener('mousedown', closeForm);
    }
    return () => {
      document.removeEventListener('mousedown', closeForm);
    };
  }, [isFormVisible]);

  const handleSuccess = () => {
    // Close the form modal and show the success modal.
    setFormVisible(false);
    setShowSuccessModal(true);
  };

  const handleCloseForm = () => {
    setFormVisible(false);
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
  };

  return (
    <>
      {/* Floating contact button — real <button> with an accessible name. Wrapped
          in <aside> so it sits inside a landmark (IBM aria_content_in_landmark). */}
      <aside aria-label="Quick contact">
      <button
        type="button"
        className="FloatingContactButton-speechBubble"
        onClick={toggleFormVisibility}
        ref={buttonRef}
        aria-label={isFormVisible ? 'Close contact form' : 'Open contact form'}
        aria-expanded={isFormVisible}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="35"
          height="35"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="FloatingContactButton-icon"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      </aside>

      {/* Contact Form Modal */}
      {isFormVisible && (
        <div
          className="FloatingContactButton-contactFormContainer"
          ref={formRef}
        >
          <ContactFormModal isOpen={isFormVisible} onClose={handleCloseForm} onSuccess={handleSuccess} />
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && <SuccessModal onClose={handleCloseSuccess} />}
    </>
  );
};

export default FloatingContactButton;
